import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { authFetch } from '../auth/api';
import { formatLapSeconds, parseLapTimeSeconds } from '../analytics/lapTime';
import { LapSeries, LapTraceChart } from './charts/LapTraceChart';
import { PaceDistribution, PaceEntry } from './charts/PaceDistribution';
import './EventStats.css';

interface TeamStats {
  teamId: string;
  kartNumber: string;
  category: string;
  drivers: string[];
  lapCount: number;
  bestSeconds: number | null;
  meanSeconds: number | null;
  cleanAverageSeconds: number | null;
  medianSeconds: number | null;
  consistencyCv: number | null;
}

interface DriverStats {
  teamId: string;
  kartNumber: string;
  driver: string;
  lapCount: number;
  bestSeconds: number | null;
  cleanAverageSeconds: number | null;
}

interface EventStatsDto {
  eventId: string;
  lapCount: number;
  teams: TeamStats[];
  drivers: DriverStats[];
}

interface LapRow {
  teamId: string;
  lapNumber: number;
  lapTime: string;
}

interface EventStatsProps {
  eventId: string;
}

function formatSeconds(seconds: number | null): string {
  return seconds === null ? '—' : formatLapSeconds(seconds);
}

export function consistencyLabel(cv: number | null): { text: string; cls: string } {
  if (cv === null) return { text: '—', cls: '' };
  // Same thresholds the live grid uses, so a debrief reads like the race did.
  const label =
    cv < 0.006 ? 'excellent' : cv < 0.012 ? 'good' : cv < 0.02 ? 'fair' : 'poor';
  return {
    text: `${label} · ${(cv * 100).toFixed(2)}%`,
    cls: `es-consistency--${label}`,
  };
}

/**
 * Post-race pace analysis, driven by the aggregate endpoint rather than by
 * pulling every lap into the browser. Individual laps are fetched only for the
 * one team whose trace is on screen.
 */
export const EventStats: React.FC<EventStatsProps> = ({ eventId }) => {
  const [stats, setStats] = useState<EventStatsDto | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const [tracedTeamId, setTracedTeamId] = useState<string | null>(null);
  const [tracedLaps, setTracedLaps] = useState<number[] | null>(null);
  const [traceLoading, setTraceLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    authFetch(`/events/${eventId}/stats`)
      .then((res) => {
        if (!res.ok) throw new Error(`Could not load stats (HTTP ${res.status})`);
        return res.json();
      })
      .then((data: EventStatsDto) => {
        if (cancelled) return;
        setStats(data);
        setTracedTeamId((prev) => prev ?? data.teams[0]?.teamId ?? null);
      })
      .catch((err: Error) => !cancelled && setError(err.message))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [eventId]);

  useEffect(() => {
    if (!tracedTeamId) return;
    let cancelled = false;
    setTraceLoading(true);
    authFetch(`/events/${eventId}/laps?teamId=${encodeURIComponent(tracedTeamId)}&limit=5000`)
      .then((res) => (res.ok ? res.json() : []))
      .then((rows: LapRow[]) => {
        if (cancelled) return;
        setTracedLaps(
          rows
            .map((r) => parseLapTimeSeconds(r.lapTime))
            .filter((s): s is number => s !== null),
        );
      })
      .catch(() => !cancelled && setTracedLaps([]))
      .finally(() => !cancelled && setTraceLoading(false));
    return () => {
      cancelled = true;
    };
  }, [eventId, tracedTeamId]);

  const paceEntries = useMemo<PaceEntry[]>(
    () =>
      (stats?.teams ?? [])
        .filter((t) => t.cleanAverageSeconds !== null)
        .map((t) => ({
          teamId: t.teamId,
          kartNumber: t.kartNumber,
          label: t.drivers[0] ?? `Kart ${t.kartNumber}`,
          paceSeconds: t.cleanAverageSeconds as number,
          category: t.category,
        })),
    [stats],
  );

  const tracedTeam = stats?.teams.find((t) => t.teamId === tracedTeamId) ?? null;

  const traceSeries = useMemo<LapSeries[]>(() => {
    if (!tracedTeam || !tracedLaps?.length) return [];
    return [
      {
        teamId: tracedTeam.teamId,
        label: tracedTeam.drivers.join(', ') || 'Unknown driver',
        kartNumber: tracedTeam.kartNumber,
        // The lap table has no stint boundaries; one continuous run is honest.
        stints: [tracedLaps],
      },
    ];
  }, [tracedTeam, tracedLaps]);

  const selectTeam = useCallback((teamId: string) => {
    setTracedTeamId(teamId);
    setTracedLaps(null);
  }, []);

  if (loading) return <div className="events-empty">Loading pace analysis…</div>;
  if (error) return <div className="events-error">{error}</div>;
  if (!stats || stats.teams.length === 0) {
    return <div className="events-empty">No laps recorded, so there is nothing to analyse.</div>;
  }

  return (
    <div className="es-root">
      <section className="es-section">
        <h3 className="events-section-title">
          Field pace <span className="es-sub">{stats.teams.length} karts · {stats.lapCount.toLocaleString()} laps</span>
        </h3>
        <PaceDistribution
          entries={paceEntries}
          ourTeamIds={tracedTeamId ? new Set([tracedTeamId]) : undefined}
          height={110}
        />
      </section>

      <section className="es-section">
        <h3 className="events-section-title">
          Lap trace
          {tracedTeam && (
            <span className="es-sub">
              #{tracedTeam.kartNumber} · {tracedTeam.drivers.join(', ')}
            </span>
          )}
        </h3>
        {traceLoading ? (
          <div className="events-empty">Loading laps…</div>
        ) : (
          <LapTraceChart series={traceSeries} />
        )}
        <p className="es-hint">Pick a row below to trace that kart.</p>
      </section>

      <section className="es-section">
        <h3 className="events-section-title">By kart</h3>
        <div className="events-table-wrapper">
          <table className="events-table es-table">
            <thead>
              <tr>
                <th className="num">Kart</th>
                <th>Category</th>
                <th className="num">Laps</th>
                <th className="num">Best</th>
                <th className="num">Clean avg</th>
                <th className="num">Median</th>
                <th>Consistency</th>
                <th>Drivers</th>
              </tr>
            </thead>
            <tbody>
              {stats.teams.map((t) => {
                const consistency = consistencyLabel(t.consistencyCv);
                return (
                  <tr
                    key={t.teamId}
                    className={`es-row${t.teamId === tracedTeamId ? ' es-row--traced' : ''}`}
                    onClick={() => selectTeam(t.teamId)}
                    title="Trace this kart"
                  >
                    <td className="num">{t.kartNumber || '—'}</td>
                    <td>{t.category || '—'}</td>
                    <td className="num">{t.lapCount}</td>
                    <td className="num">{formatSeconds(t.bestSeconds)}</td>
                    <td className="num">{formatSeconds(t.cleanAverageSeconds)}</td>
                    <td className="num">{formatSeconds(t.medianSeconds)}</td>
                    <td className={consistency.cls}>{consistency.text}</td>
                    <td className="es-drivers">{t.drivers.join(', ') || '—'}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </section>

      <section className="es-section">
        <h3 className="events-section-title">
          By driver <span className="es-sub">{stats.drivers.length} drivers</span>
        </h3>
        <div className="events-table-wrapper">
          <table className="events-table es-table">
            <thead>
              <tr>
                <th>Driver</th>
                <th className="num">Kart</th>
                <th className="num">Laps</th>
                <th className="num">Best</th>
                <th className="num">Clean avg</th>
              </tr>
            </thead>
            <tbody>
              {stats.drivers.map((d) => (
                <tr key={`${d.teamId}-${d.driver}`}>
                  <td>{d.driver}</td>
                  <td className="num">{d.kartNumber || '—'}</td>
                  <td className="num">{d.lapCount}</td>
                  <td className="num">{formatSeconds(d.bestSeconds)}</td>
                  <td className="num">{formatSeconds(d.cleanAverageSeconds)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
};

export default EventStats;
